/**
 * Codex house schema — the genre-neutral spine (ADR-006).
 *
 * Liturgy is the first genre; canons/policy reuse these exact types later by
 * widening `Genre` + the `NodeType` vocabulary only. Load-bearing invariants:
 *
 *  1. A node `id` is a permanent, opaque slug, assigned once and NEVER reused.
 *     Deletes are tombstoned (`deleted`), not hard-removed, so historical
 *     revisions and commentary stay resolvable.
 *  2. Display numbers are COMPUTED at render (walk `orderIndex`; rubrics take a
 *     running integer; headings unnumbered; elements render under their rubric).
 *     Never stored; commentary never targets a number — only a node `id`, and for
 *     a word-level note, a `run.anchorId` (NOT a literal word offset), so editing
 *     surrounding text can never orphan an anchor.
 */

/** Document genres. Liturgy ships now; the rest are reserved (schema-ready). */
export type Genre = 'liturgy' | 'canon' | 'policy';

/** Liturgy element vocabulary (from the pilot service + PoC). */
export type NodeType =
  | 'note' // front-matter note
  | 'heading' // section heading (unnumbered)
  | 'rubric' // numbered direction (red by default)
  | 'sub-rubric' // direction (red) with NO number — may still hold nested lines
  | 'spoken' // a said line (leader)
  | 'response' // congregational / bold response
  | 'prayer' // multi-line prayer block
  | 'spoken-block' // multi-line spoken block
  | 'alternative'; // labelled alternative block

/** Commentary register — palette-coloured tags. The first four are the note's
 *  status/register; the rest are the Commission's liturgy-rationale topics. */
export type CommentaryKind =
  | 'decision'
  | 'rationale'
  | 'discussion'
  | 'open'
  | 'logic'
  | 'purpose'
  | 'flow'
  | 'content'
  | 'sources'
  | 'scripture'
  | 'origin';

/** Audience tiers a commentary entry can be scoped to. */
export type Audience = 'commission' | 'clergy' | 'public';

/**
 * Palette roles a block's text colour may take — constrained, never free hex,
 * so colour stays on-brand and re-themes. Defaults by type: `rubric` → 'rubric'
 * (red), every other type → 'ink' (black); an author may override per block.
 */
export type ColourRole = 'ink' | 'rubric' | 'structural' | 'accent' | 'muted';

/**
 * A run of text carrying the constrained inline marks (bold / italic) and an
 * optional anchor binding. A word-level commentary anchor binds here via
 * `anchorId === Commentary.id`, so the binding travels with the text it marks.
 *
 * Binding ≠ marker position. By DEFAULT the visible anchor marker sits at the
 * END of the line (clean reading); the author may pull it onto this exact word
 * by setting `anchorAtWord` — the binding (and the tether target) is unchanged
 * either way.
 */
export interface InlineRun {
  text: string;
  bold?: boolean;
  italic?: boolean;
  anchorId?: string;
  /** Render the marker inline at this word instead of at the line end (default). */
  anchorAtWord?: boolean;
}

/** One line within a block; `response` marks a bold congregational line. */
export interface InlineLine {
  runs: InlineRun[];
  response?: boolean;
}

export interface DocDocument {
  id: string;
  genre: Genre;
  title: string;
  subtitle: string | null;
  status: string; // 'draft' | 'published' | …
  updatedAt: string; // ISO
  // Free-text explanatory notes, shown to every reader below the service and in
  // the printed copy (distinct from audience-filtered commentary). Inline
  // bold/italic only; [] when empty. The document-list read omits it (→ [] there).
  notes: InlineLine[];
}

export interface DocNode {
  id: string; // permanent opaque id — never reused
  documentId: string;
  parentId: string | null; // the rubric for an element; null at top level
  orderIndex: number; // REAL so inserts don't renumber siblings
  type: NodeType;
  lines: InlineLine[]; // text content — one line for single-line types
  label: string | null; // e.g. "Or the two great commandments:"
  colour?: ColourRole; // optional per-block override (else default by type)
  deleted: boolean; // tombstone
  // Stored in D1 as TEXT: `lines`/`label` serialise to the `content` column as
  // JSON; the worker's db layer maps to/from this logical shape.
}

export interface Commentary {
  id: string;
  documentId: string;
  targetNodeId: string; // the HOME anchor — where the note is authored/edited
  span: string | null; // human-readable anchored phrase (display/export label only)
  kind: CommentaryKind;
  audience: Audience;
  body: string;
  author: string | null;
  createdAt: string; // ISO
  deleted: boolean;
  /**
   * If set, this entry is prior discussion *behind* another note (the head, by
   * id) — shown collapsed under it and expandable, not as its own anchor. Each
   * thread entry keeps its OWN audience, so commission-only discussion stays
   * hidden from clergy/public even when the decision itself is visible.
   */
  relatesTo: string | null;
  /**
   * ADDITIONAL node ids this same note is also pinned to (beyond `targetNodeId`).
   * The note body is shared — edit it once, it updates at every anchor. Filtered
   * to live nodes server-side; `[]` for a single-anchor note. Inherits the note's
   * audience at every anchor (no separate gate). Threads (`relatesTo` set) never
   * carry their own anchors — they ride under their head.
   */
  extraTargets: string[];
}

export interface Revision {
  id: string;
  documentId: string;
  snapshot: string; // full CanonicalDocument JSON at save time
  label: string | null;
  author: string | null;
  createdAt: string; // ISO
}

/**
 * The canonical serialised form: what the editor loads, what the nightly cron
 * writes to the git archive, and the unit a `Revision.snapshot` holds. Killing
 * the app must never trap the content — this is the software-independent record.
 */
export interface CanonicalDocument {
  document: DocDocument;
  nodes: DocNode[];
  commentary: Commentary[];
}

/**
 * Payload for bulk-importing a whole document (e.g. a parsed .docx) in one call.
 * Nodes + commentary reference each other by caller-assigned `tempId`s; the
 * worker mints the real opaque ids and resolves the links on insert.
 */
export interface ImportNode {
  tempId: string;
  parentTempId: string | null;
  orderIndex: number;
  type: NodeType;
  lines: InlineLine[];
  label?: string | null;
}
export interface ImportCommentary {
  targetTempId: string;
  kind: CommentaryKind;
  audience: Audience;
  body: string;
  span?: string | null;
}
export interface ImportDocument {
  title: string;
  subtitle?: string | null;
  genre: Genre;
  nodes: ImportNode[];
  commentary: ImportCommentary[];
}

/** Default text colour for a node type (rubric red; everything else black). */
export function defaultColour(type: NodeType): ColourRole {
  return type === 'rubric' || type === 'sub-rubric' ? 'rubric' : 'ink';
}

/** The effective colour role for a node (explicit override, else the type default). */
export function nodeColour(node: Pick<DocNode, 'type' | 'colour'>): ColourRole {
  return node.colour ?? defaultColour(node.type);
}
